import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, TableSortLabel } from '@mui/material';
import { getSalesFunnel } from '../../services/salesFunnel';
import Loading from './Loading';
import { useTranslation } from 'react-i18next';

const SalesFunnelReport = ({ user }) => {

	const { t } = useTranslation();
	
	const [funnelData, setFunnelData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [order, setOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState('sales_count');

	const getSalesFunnelData = async () => {
		setIsLoading(true);
		try {
			const result = await getSalesFunnel(user._id);
			setFunnelData(result);
		} catch (error) {
			console.error('Error fetching sales funnel data:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getSalesFunnelData();
	}, [user._id]);

	const handleSort = (property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const sortedData = [...funnelData].sort((a, b) => {
		if (order === 'asc') {
			return a[orderBy] < b[orderBy] ? -1 : 1;
		}
		return a[orderBy] > b[orderBy] ? -1 : 1;
	});

	return (
		<Box>
			{/* Table for larger screens */}
			<TableContainer                 
				sx={{
                    display: { xs: 'none', md: 'flex' },
                    padding: 2
            }}>
				<Table>
					<TableHead>
						<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'campaignName'}
									direction={orderBy === 'campaignName' ? order : 'asc'}
									onClick={() => handleSort('campaignName')}
								>
									{t('salesFunnel.campaignName')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'clickId'}
									direction={orderBy === 'clickId' ? order : 'asc'}
									onClick={() => handleSort('clickId')}
								>
									{t('salesFunnel.clickId')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'keyword'}
									direction={orderBy === 'keyword' ? order : 'asc'}
									onClick={() => handleSort('keyword')}
								>
									{t('salesFunnel.keyword')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'accesses_count'}
									direction={orderBy === 'accesses_count' ? order : 'asc'}
									onClick={() => handleSort('accesses_count')}
								>
									{t('salesFunnel.accesses')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'clicks_count'}
									direction={orderBy === 'clicks_count' ? order : 'asc'}
									onClick={() => handleSort('clicks_count')}
								>
									{t('salesFunnel.clicks')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'sales_count'}
									direction={orderBy === 'sales_count' ? order : 'asc'}
									onClick={() => handleSort('sales_count')}
								>
									{t('salesFunnel.sales')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'percentage_of_abandon'}
									direction={orderBy === 'percentage_of_abandon' ? order : 'asc'}
									onClick={() => handleSort('percentage_of_abandon')}
								>
									{t('salesFunnel.abandonRate')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'percentage_of_conversion'}
									direction={orderBy === 'percentage_of_conversion' ? order : 'asc'}
									onClick={() => handleSort('percentage_of_conversion')}
								>
									{t('salesFunnel.conversionRate')}
								</TableSortLabel>
							</TableCell>
							<TableCell>
								<TableSortLabel
									active={orderBy === 'total_sales'}
									direction={orderBy === 'total_sales' ? order : 'asc'}
									onClick={() => handleSort('total_sales')}
								>
									{t('salesFunnel.total')}
								</TableSortLabel>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedData.map((data) => (
							<TableRow key={data.clickId}>
								<TableCell>{data.campaignName}</TableCell>
								<TableCell>{data.clickId}</TableCell>
								<TableCell>{data.keyword}</TableCell>
								<TableCell>{data.accesses_count}</TableCell>
								<TableCell>{data.clicks_count}</TableCell>
								<TableCell>{data.sales_count}</TableCell>
								<TableCell>{data.percentage_of_abandon}</TableCell>
								<TableCell>{data.percentage_of_conversion}</TableCell>
								<TableCell>{`R$ ${data.total_sales}`}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Table for smaller screens */}
			<Box
				sx={{
					display: { xs: 'block', md: 'none' },
					padding: 2,
					maxHeight: '80vh',
					overflowY: 'auto',
				}}
				>
				{sortedData.map((data) => (
					<Box
					key={data.clickId}
					sx={{
						border: '1px solid #ddd',
						borderRadius: 2,
						mb: 2,
						p: 2,
						boxShadow: 1,
						backgroundColor: '#fff',
					}}
					>
					<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
						{t('salesFunnel.campaignName')}: {data.campaignName || t('salesFunnel.noCampaign')}
					</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.clickId')}:</strong> {data.clickId}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.keyword')}:</strong> {data.keyword}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.accesses')}:</strong> {data.accesses_count}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.clicks')}:</strong> {data.clicks_count}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.sales')}:</strong> {data.sales_count}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.abandonRate')}:</strong> {data.percentage_of_abandon}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.conversionRate')}:</strong> {data.percentage_of_conversion}</Typography>
					<Typography variant="body2"><strong>{t('salesFunnel.total')}:</strong> R$ {data.total_sales}</Typography>
					</Box>
				))}
				</Box>
				<Loading text={t('salesFunnel.loading')} isLoading={isLoading} />
		</Box>
	);
};

export default SalesFunnelReport;
